import { Component, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { WidgetComponentInterface } from '../widget-component.interface';
import { AggregateMetaData, DomainModelMetaData, EnumResource, GenericServiceResponse, JsonNetDecycle, PropertyMetaData, RouteChangeParams, RoutingService, ServiceResponse, ToastMessageService, ZoomResultsDateTimeCellRendererComponent, ZoomResultsDateTimeOffsetCellRendererComponent } from '@nts/std';
import { WidgetResultSetFilterDataArgDto } from '../../../domain-models/dto/widget-result-set-filter-data-arg-dto';
import { WidgetFullScreenComponent } from '../widget-full-screen-component';
import { MetaDataUtils } from '@nts/std';
import { BehaviorSubject, firstValueFrom, from, Observable, of, Subject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { WidgetFeatures } from '../widget-component';
import { GridApi, GridOptions, ColDef, ColumnApi, ColumnState } from 'ag-grid-community';
import { LogService } from '@nts/std/src/lib/utility';
import { WidgetZoomResultValueDto } from 'src/app/dash-board/domain-models/dto/widget-zoom-result-value';
import { OutputDataOrderDto } from 'src/app/dash-board/domain-models/dto/output-data-order-dto';
import { ZoomResultsDefaultCellRendererComponent } from '@nts/std';
import { ZoomResultsBoolCellRendererComponent } from '@nts/std';
import { ZoomColumnInfo } from '@nts/std';
import { AgGridModule } from 'ag-grid-angular';
import { NgIf } from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'nts-zoom-result-widget',
  templateUrl: './zoom-result-widget.component.html',
  styleUrls: ['./zoom-result-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AgGridModule,
    NgIf
  ]
})
export class ZoomResultWidgetComponent extends WidgetFullScreenComponent implements WidgetComponentInterface {
  // #region Public Properties
  override canShowAutomaticUpdateDataSettings$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  gridOptions: GridOptions;
  title: string;
  subTitle: string;
  columnDefs = [];
  rowData = [];
  components = {
    loadingRenderer: (params) => '<img src="assets/images/nts/widget/loading.gif">'
  };
  // #endregion

  // #region Private Fields
  #orderMap: { [key: string]: number; } = {};
  #originalOrderColId: string[] = [];
  #gridApi$: BehaviorSubject<GridApi> = new BehaviorSubject<GridApi>(null);
  #gridColumnApi: ColumnApi;
  #updateGridStateRequested: Subject<void> = new Subject<void>();
  #currentTake = 0;
  #currentSkip = 0;
  #noData = false;
  #metaData: AggregateMetaData;
  #layout: OutputDataOrderDto[];
  #enumDictionaries: Map<number, EnumResource[]>;
  #init = true;
  #loadDataCache: {
    key: string;
    timestamp: number;
    observable: Observable<GenericServiceResponse<any>>;
  } = null;
  // #endregion

  // #region Constructor
  constructor(
    private cd: ChangeDetectorRef,
    toastMessageService: ToastMessageService,
    private routingService: RoutingService
  ) {
    super(toastMessageService, cd);
    this.gridOptions = {} as GridOptions;
    this.gridOptions.rowBuffer = 0;
    this.gridOptions.rowSelection = 'single';
    this.gridOptions.rowModelType = 'infinite';
    this.gridOptions.maxConcurrentDatasourceRequests = 1;
  }
  // #endregion

  // #region Lifecycle & Init

  /**
   * Inizializza il componente e gestisce il refresh della griglia.
   */
  override initComponent(refreshigData = false): Observable<boolean> {
    if (refreshigData) {
      if (this.#gridApi$.value) {
        this.#gridApi$.value.purgeInfiniteCache();
        this.#gridApi$.value.refreshInfiniteCache();
      }
      if (this.#noData) {
        this.refreshInProgress$.next(false);
        this.cd.markForCheck();
      }
    }
    return of(true).pipe(tap(async () => {
      if (!refreshigData) {
        await firstValueFrom(
          this.#gridApi$.pipe(
            untilDestroyed(this),
            filter(api => !!api)
          )
        );
        setTimeout(() => {
          this.refreshData();
        }, 500);
      }
    }));
  }
  // #endregion

  // #region Grid Events & DataSource

  /**
   * Handler per l'evento di grid ready. Imposta il datasource e gestisce lo stato delle colonne.
   */
  onGridReady(params) {
    this.#gridApi$.next(params.api as GridApi);
    this.#gridColumnApi = params.columnApi;

    this.#updateGridStateRequested
      .pipe(
        untilDestroyed(this),
        debounceTime(500)
      )
      .subscribe(async () => {
        let savedState = this.#gridColumnApi.getColumnState();
        await this.setFeatureForCurrentWidget(
          WidgetFeatures.GridState, savedState);
      });

    this.#reloadGridState();
    const self = this;

    const dataSource = {
      rowCount: null,
      getRows: async (params) => {
        LogService.debug(
          'asking for ' + params.startRow + ' to ' + params.endRow
        );

        self.#currentTake = params.endRow - params.startRow + 1;
        self.#currentSkip = params.startRow;
        const response = (await self.loadData(!self.#init).toPromise()) as WidgetZoomResultValueDto;

        if (params.startRow === 0) {
          self.#noData = response.result?.length == null;
        }

        if (self.#init) {
          await self.initView(response, false).toPromise();
        }

        if (response) {
          const rowsThisPage = self.#normalizeRow(response.result);

          let lastRow = -1;
          if (((response?.result?.length ?? 0) + params.startRow) <= params.endRow) {
            lastRow = (response?.result?.length ?? 0) + params.startRow;
          }

          params.successCallback(self.#getRowDataWithOrderedColumn(rowsThisPage), lastRow);
          if (self.#init) {
            self.#reloadGridState();
            self.#init = false;
          }
        } else {
          params.failCallback();
          LogService.warn(
            'Pagination API failed!', response
          );
        }
        self.forceHideLoader.next();
        self.cd.markForCheck();
      },
    };
    params.api.setDatasource(dataSource);
  }

  /**
   * Handler per il doppio click su una riga.
   */
  rowDoubleClicked(e: any) {
    const data = e.data as any[];
    if (this.#metaData.rootFullName) {
      const routeChangeParams = new RouteChangeParams();
      routeChangeParams.rootModelFullName = this.#metaData.rootFullName;
      routeChangeParams.inBlank = true;

      const serializedIdentity = this.#getJsonIdentity(data);
      if (serializedIdentity && this.#isJsonString(serializedIdentity)) {
        routeChangeParams.routeParam = RoutingService.encodeUrl(serializedIdentity);
      }

      routeChangeParams.queryParams = '?collapse-menu=true';

      this.routingService.routeChangeRequested.next(routeChangeParams);
    }
  }

  /**
   * Handler per il resize delle colonne.
   */
  onColumnResized(e) {
    this.#updateGridStateRequested.next();
  }

  /**
   * Handler per lo spostamento delle colonne.
   */
  onColumnMoved(e) {
    this.#updateGridStateRequested.next();
  }
  // #endregion

  // #region Data Loading & Caching

  /**
   * Esegue il refresh dei dati e aggiorna lo stato della UI.
   */
  protected override async refreshData() {
    if (this.refreshInProgress$.value === false) {
      this.refreshInProgress$.next(true);
      // Per resettare gli errori devo passare un ServiceResponse
      this.vm.loadingErrorChanged.next(new ServiceResponse());

      // Forzo la prima chiamata
      this.#currentTake = 101;
      this.#currentSkip = 0;

      await firstValueFrom(this.getLoadDataObservable(true));
      this.refreshInProgress$.next(false);

      this.cd.markForCheck();
      this.#init = true;
      await firstValueFrom(this.initComponent(true).pipe(
        untilDestroyed(this),
        tap(() => {
          this.cd.markForCheck();
        })
      ));
    }
  }

  /**
   * Inizializza la view dopo la prima chiamata dati.
   */
  protected initView(result: WidgetZoomResultValueDto, refreshigData: boolean): Observable<boolean> {
    if (result?.title && result?.result) {
      this.title = result.title;
      this.modalTitle = result.title;
      this.subTitle = result.subTitle;
      this.#metaData = result.metaData;
      this.#layout = result.layout;

      this.#buildEnumDictionaries(this.#metaData.rootMetaData);

      return from(this.#updateFieldsFromLabels(result)).pipe(
        untilDestroyed(this),
        tap(() => this.cd.markForCheck()),
        map(() => true)
      );

    } else {
      this.title = this.vm.description.value;
    }

    this.cd.markForCheck();
    return of(true);
  }

  /**
   * Restituisce un observable per il caricamento dati, con cache interna per evitare chiamate duplicate entro 500ms.
   */
  protected getLoadDataObservable<WidgetZoomResultValueDto>(
    refreshigData: boolean
  ): Observable<GenericServiceResponse<WidgetZoomResultValueDto>> {
    const widgetDataArg = this.getWidgetDataArg();
    const widgetResultSetFilterDataArgDto = new WidgetResultSetFilterDataArgDto;
    widgetResultSetFilterDataArgDto.dashBoardWidgetIdentity = widgetDataArg.dashBoardWidgetIdentity;
    widgetResultSetFilterDataArgDto.identity = widgetDataArg.identity;
    widgetResultSetFilterDataArgDto.jsonAPIArg = widgetDataArg.jsonAPIArg;
    widgetResultSetFilterDataArgDto.skip = this.#currentSkip;
    widgetResultSetFilterDataArgDto.take = this.#currentTake;

    // Genera una chiave unica per il payload
    const cacheKey = JSON.stringify(
      JsonNetDecycle.decycle(
        {
          dashBoardWidgetIdentity: widgetResultSetFilterDataArgDto.dashBoardWidgetIdentity?.code,
          identity: widgetResultSetFilterDataArgDto.identity,
          jsonAPIArg: widgetResultSetFilterDataArgDto.jsonAPIArg,
          skip: widgetResultSetFilterDataArgDto.skip,
          take: widgetResultSetFilterDataArgDto.take,
          refreshigData
        }
      )
    );

    if (
      this.#loadDataCache &&
      this.#loadDataCache.key === cacheKey &&
      (Date.now() - this.#loadDataCache.timestamp) < 500
    ) {
      return this.#loadDataCache.observable as Observable<GenericServiceResponse<WidgetZoomResultValueDto>>;
    }

    const obs = this.getApiClient().getZoomResult(
      widgetResultSetFilterDataArgDto, refreshigData
    ).pipe(
      untilDestroyed(this),
      tap((r) => this.lastUpdate = r.cache?.timestamp),
      map(r => r.response),
      tap(() => {
        this.#loadDataCache = {
          key: cacheKey,
          timestamp: Date.now(),
          observable: obs
        };
      })
    ) as Observable<GenericServiceResponse<WidgetZoomResultValueDto>>;

    return obs;
  }
  // #endregion

  // #region Grid State & Layout
  /**
   * Ricarica lo stato delle colonne della griglia se presente.
   */
  async #reloadGridState() {
    if (this.#gridColumnApi) {
      const savedState = await this.getFeatureForCurrentWidget<ColumnState[]>(
        WidgetFeatures.GridState);
      if (savedState?.length > 0) {
        // restore the column state
        this.#gridColumnApi.applyColumnState({ state: savedState });
      }
    }
  }

  /**
   * Aggiorna i campi della griglia in base alle label e all'ordine delle colonne.
   */
  async #updateFieldsFromLabels(widgetZoomResultValueDto: WidgetZoomResultValueDto) {
    const cellValueGetter = (params: any, index: number) => {
      const data = params.data;
      if (data) {
        return data?.[index] ?? '';
      } else {
        return null;
      }
    };

    this.#originalOrderColId = widgetZoomResultValueDto.layout.map((outputDataOrderDto: OutputDataOrderDto) => this.getWidgetUniqueKey() + '_' + outputDataOrderDto.propertyName);

    const columnDefs = widgetZoomResultValueDto.layout.map((outputDataOrderDto: OutputDataOrderDto) => ({ colId: this.getWidgetUniqueKey() + '_' + outputDataOrderDto.propertyName, ...outputDataOrderDto }));

    const savedState = await this.getFeatureForCurrentWidget<ColumnState[]>(
      WidgetFeatures.GridState
    );

    if (savedState?.length > 0) {

      // Resetto la mappa
      this.#orderMap = {};

      for (const [index, item] of savedState.entries()) {
        this.#orderMap[item.colId] = index;
      }

      // Ordinare l'array columnDefs in base alla mappa creata
      columnDefs.sort((a, b) => {
        const orderA = this.#orderMap[a.colId] !== undefined ? this.#orderMap[a.colId] : Infinity;
        const orderB = this.#orderMap[b.colId] !== undefined ? this.#orderMap[b.colId] : Infinity;
        return orderA - orderB;
      });
    }

    this.columnDefs =

      columnDefs.map((outputDataOrderDto: (OutputDataOrderDto & { colId: string; }), index: number) => ({

        /**
         * The unique ID to give the column.
         * This is optional. If missing, the ID will default to the field.
         * If both field and colId are missing, a unique ID will be generated.
         * This ID is used to identify the column in the API for sorting, filtering etc.
         */
        colId: outputDataOrderDto.colId,

        //field: outputDataOrderDto.propertyName,

        headerName: outputDataOrderDto.caption,
        field: index.toString(),
        cellRendererParams: {
          columnInfo: this.#getColumnInfo(outputDataOrderDto, index)
        },

        resizable: true,

        sortable: false,

        hide: !outputDataOrderDto.isVisible,

        /**
         * Set to true to block making column visible / hidden via the UI
         * (API will still work). Default: false
         */
        lockVisible: true,

        /**
         * Function or expression. Gets the value from your data for display.
         */
        valueGetter: (params: any) => cellValueGetter(params, index),

        cellRenderer: this.#getCellRendererFramework(
          outputDataOrderDto.propertyName
        ),
        /**
         * Callback to select which cell renderer to be used for a given row within the same column.
         */
        cellRendererSelector: (params: any) => {

          if (params.value == null) {
            return {
              component: 'loadingRenderer',
            };
          }
          return null;
        }
      } as ColDef));
  }

  // #endregion

  // #region Utility Methods

  /**
   * Verifica se una stringa è un JSON valido.
   */
  #isJsonString(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  /**
   * Restituisce i dati delle righe ordinati secondo la mappa delle colonne.
   */
  #getRowDataWithOrderedColumn(data: (string | number | boolean)[][]): any {
    return data.map((row) => {
      const orderedRowWithIndex = row.map((value, index) => ({ value, index })).sort((a, b) => {
        const orderA = this.#orderMap[this.#originalOrderColId[a.index]] !== undefined ? this.#orderMap[this.#originalOrderColId[a.index]] : Infinity;
        const orderB = this.#orderMap[this.#originalOrderColId[b.index]] !== undefined ? this.#orderMap[this.#originalOrderColId[b.index]] : Infinity;
        return orderA - orderB;
      });
      return orderedRowWithIndex.map((f) => f.value);
    });
  }

  /**
   * Costruisce i dizionari degli enum per la visualizzazione.
   */
  #buildEnumDictionaries(domainModelMetaData: DomainModelMetaData) {

    this.#enumDictionaries = new Map<number, EnumResource[]>();

    this.#checkEnums(domainModelMetaData, '');
  }

  /**
   * Ricorsivamente popola i dizionari degli enum.
   */
  #checkEnums(domainModelMetaData: DomainModelMetaData, currentPath: string, processedExternals: string[] = []) {

    domainModelMetaData.enums.forEach(en => {
      const columnIndex = this.#layout.indexOf(this.#layout.filter(item => {
        const rootPath = item.propertyName.split('.').slice(0, -1).join('.');
        return (rootPath ?? '') === currentPath && item.propertyName === en.name;
      })[0]);

      if (columnIndex >= 0) {
        this.#enumDictionaries.set(columnIndex, en.valuesResource);
      }
    });

    domainModelMetaData.internalCollections.forEach(internalCollection => {
      this.#checkEnums(internalCollection.dependentMetaData,
        currentPath.length > 0 ? (currentPath + '.' + internalCollection.principalPropertyName) : internalCollection.principalPropertyName,
        processedExternals);
    });

    domainModelMetaData.internalRelations.forEach(internalRelation => {
      this.#checkEnums(
        internalRelation.dependentMetaData,
        currentPath.length > 0 ? (currentPath + '.' + internalRelation.principalPropertyName) : internalRelation.principalPropertyName,
        processedExternals);
    });

    domainModelMetaData.externals.forEach(external => {
      if (processedExternals.indexOf(external.dependentAggregateMetaData.rootFullName) === -1) {
        processedExternals.push(external.dependentAggregateMetaData.rootFullName);
        this.#checkEnums(external.dependentAggregateMetaData.rootMetaData,
          currentPath.length > 0 ? (currentPath + '.' + external.principalPropertyName) : external.principalPropertyName,
          processedExternals);
      }
    });
  }

  /**
   * Restituisce le informazioni di colonna per la griglia.
   */
  #getColumnInfo(outputDataOrderDto: OutputDataOrderDto, index: number): ZoomColumnInfo {
    const columnInfo = new ZoomColumnInfo(outputDataOrderDto.propertyName, index);
    columnInfo.header = outputDataOrderDto.caption;
    columnInfo.isVisible = outputDataOrderDto.isVisible;
    columnInfo.position = outputDataOrderDto.position;
    columnInfo.propertyTypeName = this.#getPropertyMetaData(outputDataOrderDto.propertyName)?.name;
    //column.settings = SettingsType.Default;
    return columnInfo;
  }

  /**
   * Restituisce il componente di cell renderer corretto in base al tipo.
   */
  #getCellRendererFramework(propertyName: string) {
    const propertyMetaData = this.#getPropertyMetaData(propertyName);
    switch (propertyMetaData?.getType()) {
      case 'Bool':
        return ZoomResultsBoolCellRendererComponent;
      case 'DateTime':
        return ZoomResultsDateTimeCellRendererComponent;
      case 'DateTimeOffset':
        return ZoomResultsDateTimeOffsetCellRendererComponent;
      default:
        return ZoomResultsDefaultCellRendererComponent;
    }
  }

  /**
   * Restituisce i metadati di una proprietà dato il nome.
   */
  #getPropertyMetaData(propertyName: string): PropertyMetaData {
    return MetaDataUtils.getPropertyMetaDataFromPath(this.#metaData.rootMetaData, propertyName);
  }

  /**
   * Restituisce la rappresentazione JSON dell'identità di una riga.
   */
  #getJsonIdentity(item: Object) {
    const identity = {};
    this.#metaData.rootMetaData.identityNames.forEach((propertyName: string) => {
      const propertyNameInCamelCase = MetaDataUtils.toCamelCase(propertyName);
      const columnIndex = this.#layout.findIndex(column => column.propertyName === propertyName);
      let val = item[columnIndex];
      identity[propertyNameInCamelCase] = val;
    });
    return JSON.stringify(identity);
  }

  /**
   * Normalizza i valori delle righe sostituendo gli enum con i display value.
   * Restituisce un clone dei dati senza modificare l'array originale.
   */
  #normalizeRow(rows: any[]) {
    // Clona l'array di righe per evitare modifiche ai dati originali
    const normalizedRows = rows.map(row => [...row]);

    if (this.#enumDictionaries != null) {
      for (const row of normalizedRows) {
        this.#enumDictionaries.forEach((value, colIndex) => {
          const enumResources = this.#enumDictionaries.get(colIndex);
          if (enumResources) {
            const enumResource = enumResources.find((e: EnumResource) => e.enumValue === row[colIndex]);
            if (enumResource) {
              row[colIndex] = enumResource.displayValue;
            } else {
              LogService.warn(`ERRORE: enumValue ${row[colIndex]} non trovato!`, enumResource, this.#enumDictionaries);
            }
          } else {
            LogService.warn(`ERRORE: indice ${colIndex} non trovato in enumDictionaries!`, this.#enumDictionaries);
          }
        });
      }
    }
    return normalizedRows;
  }
  // #endregion
}
